import { useAssignedCurricula } from '../../utils/useAssignedCurricula';
import { StudentWorkPrinterProvider } from '../../../library/printing/StudentWorkPrinter';
import React, { useEffect, useMemo, useState } from 'react';
import { Squad, User } from '../../../../types';
import { getCurriculumAdapterById } from '../../../curriculum/adapters/registry';
import { getSquadCurriculumId, isDemoExperienceSquad } from '../../utils/curriculum';
import {
  formatSquadSubtitle,
  getDefaultPagerIndex,
  resolveSessionPlan,
} from '../../utils/sessionPlan';
import { ClassroomTeam, PortfolioEntry } from '../../classroom/types';
import { ClassroomTeamsService } from '../../classroom/ClassroomTeamsService';
import { ClassroomPortfolioService } from '../../classroom/ClassroomPortfolioService';
import BoardView from './BoardView';
import ClassesView from './ClassesView';
import PortfoliosView from './PortfoliosView';
import './classroomDashboard.css';
import type { ManagedCurriculum } from '../../../curriculum/managedCurricula';
import { subscribeManagedCurriculum } from '../../../curriculum/services/curriculumService';
import DemoInfoView from './DemoInfoView';
import AiUploadQueueStatus from './AiUploadQueueStatus';
import AvatarPickerModal from './AvatarPickerModal';
import { ClassroomAvatarService } from '../../classroom/ClassroomAvatarService';

export type ClassroomTab = 'demo_info' | 'board' | 'classes' | 'portfolios';

interface ClassroomDashboardProps {
  squad: Squad;
  students: User[];
  logisticsIncomplete?: boolean;
  onSquadDetails: () => void;
  initialTab?: ClassroomTab;
  onAddStudent?: () => void;
}

const ClassroomDashboard: React.FC<ClassroomDashboardProps> = ({
  squad,
  students,
  logisticsIncomplete,
  onSquadDetails,
  initialTab = 'board',
  onAddStudent,
}) => {
  const [tab, setTab] = useState<ClassroomTab>(initialTab);
  const [teams, setTeams] = useState<ClassroomTeam[]>([]);
  const [entries, setEntries] = useState<PortfolioEntry[]>([]);
  const [focusTeamId, setFocusTeamId] = useState<string | null>(null);
  const [dataError, setDataError] = useState<string | null>(null);
  const [managedCurriculum, setManagedCurriculum] = useState<ManagedCurriculum | null>(null);
  const [avatarOpen,setAvatarOpen]=useState(false), [avatarBusy,setAvatarBusy]=useState(false);
  const [squadAvatar,setSquadAvatar]=useState(squad.classroomAvatarUrl || '');
  useEffect(()=>setSquadAvatar(squad.classroomAvatarUrl || ''),[squad.classroomAvatarUrl]);
  const saveAvatar=async(url:string,kind:'muse'|'upload')=>{setAvatarBusy(true);try{await ClassroomAvatarService.setSquad(squad.id,url,kind);setSquadAvatar(url);setAvatarOpen(false);}finally{setAvatarBusy(false);}};

  const isDemo = isDemoExperienceSquad(squad);
  const curriculumId = isDemo && squad.demoExperienceId
    ? squad.demoExperienceId
    : getSquadCurriculumId(squad);
  const adapter = useMemo(
    () => getCurriculumAdapterById(curriculumId),
    [curriculumId]
  );
  useEffect(() => subscribeManagedCurriculum(curriculumId, setManagedCurriculum, (error) => {
    console.warn('Using bundled curriculum fallback.', error);
    setManagedCurriculum(null);
  }), [curriculumId]);
  const baseLevels = managedCurriculum?.published !== false && managedCurriculum?.levels?.length
    ? managedCurriculum.levels
    : adapter.levels;
  const levels = useAssignedCurricula(squad, curriculumId, baseLevels);

  const plan = useMemo(
    () => resolveSessionPlan(squad, levels, curriculumId),
    [squad, levels, curriculumId]
  );

  const [pagerIndex, setPagerIndex] = useState(() => getDefaultPagerIndex(plan));

  useEffect(() => {
    setPagerIndex(getDefaultPagerIndex(plan));
  }, [squad.id, squad.classStatus, squad.currentLessonId, plan]);

  useEffect(() => {
    setTab(isDemo ? 'demo_info' : initialTab);
  }, [initialTab, isDemo, squad.id]);

  useEffect(() => {
    const unsubTeams = ClassroomTeamsService.subscribe(
      squad.id,
      setTeams,
      (err) => {
        console.error(err);
        setDataError('Could not load teams. Deploy latest Firestore rules if this persists.');
      }
    );
    const unsubEntries = ClassroomPortfolioService.subscribe(
      squad.id,
      setEntries,
      (err) => {
        console.error(err);
        setDataError('Could not load portfolios. Deploy latest Firestore rules if this persists.');
      }
    );
    return () => {
      unsubTeams();
      unsubEntries();
    };
  }, [squad.id]);

  const subtitle = formatSquadSubtitle(squad);
  const demoLocationName = String(squad.locationName || squad.schoolName || squad.location?.name || '').trim();
  const demoAddress = String(squad.address || squad.location?.address || '').trim();
  const demoFlyer = squad.demoFlyer?.url ? squad.demoFlyer : managedCurriculum?.levels?.[0]?.worksheet;

  const openPortfolio = (teamId: string) => {
    setFocusTeamId(teamId);
    setTab('portfolios');
  };

  return (
    <StudentWorkPrinterProvider squadId={squad.id} squadName={squad.title || 'Squad'} teams={teams} entries={entries} plan={plan}><div className="classroom-dashboard">
      <div className="cd-screen">
        <div className="cd-head">
          <div className="cd-squad-identity"><button type="button" className="cd-squad-avatar" onClick={()=>setAvatarOpen(true)} aria-label="Choose squad avatar">{squadAvatar?<img src={squadAvatar} alt=""/>:<span>＋</span>}</button><div>
            <h2 className="cd-title">{squad.title}</h2>
            {subtitle ? <div className="cd-sub">{subtitle}</div> : null}
            {isDemo && <div className="cd-demo-head-location">
              <strong>{demoLocationName || 'Location to be confirmed'}</strong>
              <span>{demoAddress || 'Address to be confirmed'}</span>
            </div>}
          </div></div>
          <div className="cd-head-actions">
            <button type="button" className="cd-btn" onClick={onSquadDetails}>
              ≡ Squad details
              {logisticsIncomplete ? (
                <span className="cd-incomplete-badge" title="Teaching logistics incomplete">
                  !
                </span>
              ) : null}
            </button>
            {onAddStudent && <button type="button" className="cd-btn cd-btn-primary" onClick={onAddStudent}>＋ Add student</button>}
            {isDemo && (demoFlyer?.url ? (
              <a className="cd-demo-flyer-head-button" href={demoFlyer.url} target="_blank" rel="noreferrer">
                <strong>🖨 Print copies of this flyer</strong>
                <span>For the demo</span>
              </a>
            ) : (
              <button type="button" className="cd-demo-flyer-head-button is-missing" disabled>
                <strong>Ask About Flyers</strong>
                <span>No flyer is attached</span>
              </button>
            ))}
          </div>
        </div>

        <div className="cd-tabs" role="tablist">
          {(
            [
              ...(isDemo ? [['demo_info', 'Demo Info'] as const] : []),
              ['board', 'Board'],
              ['classes', 'Lessons'],
              ['portfolios', 'Portfolios'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={`cd-tab ${tab === id ? 'is-on' : ''}`}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </div>

        {dataError ? <div className="cd-banner cd-banner-warn">{dataError}</div> : null}

        {tab === 'demo_info' && managedCurriculum?.kind === 'demo_experience' && <DemoInfoView curriculum={managedCurriculum} />}
        {tab === 'demo_info' && managedCurriculum?.kind !== 'demo_experience' && <div className="cd-banner cd-banner-warn">Choose a published demo experience in Squad details to see its Demo Info.</div>}

        {tab === 'board' && (
          <BoardView
            squad={squad}
            students={students}
            teams={teams}
            entries={entries}
            plan={plan}
            pagerIndex={Math.min(pagerIndex, Math.max(0, plan.slots.length - 1))}
            onPagerChange={setPagerIndex}
            levels={levels}
            onOpenPortfolio={openPortfolio}
          />
        )}
        {tab === 'classes' && (
          <ClassesView
            plan={plan}
            levels={levels}
            entries={entries}
          />
        )}
        {tab === 'portfolios' && (
          <PortfoliosView
            squadId={squad.id}
            squadName={squad.title || ''}
            teams={teams}
            entries={entries}
            plan={plan}
            liveSessionId={squad.liveSession?.sessionId || null}
            classStatus={squad.classStatus}
            currentLessonId={squad.currentLessonId}
            focusTeamId={focusTeamId}
            onFocusConsumed={() => setFocusTeamId(null)}
          />
        )}
      </div>
      <AiUploadQueueStatus />
      <AvatarPickerModal isOpen={avatarOpen} title="Choose squad avatar" busy={avatarBusy} onClose={()=>setAvatarOpen(false)} onMuse={url=>void saveAvatar(url,'muse')} onUpload={file=>void (async()=>{setAvatarBusy(true);try{const url=await ClassroomAvatarService.upload(squad.id,'squad',file);await saveAvatar(url,'upload');}finally{setAvatarBusy(false);}})()} />
    </div></StudentWorkPrinterProvider>
  );
};

export default ClassroomDashboard;
